import { useState, useEffect, useCallback } from 'react';
import {
  AUTO_PATH_LAYER,
  CUSTOM_PATH_LAYER,
  POINTS_CIRCLE_LAYER,
  POINTS_SYMBOL_LAYER,
  DURATION_LABELS_LAYER,
  CONNECTIONS_LAYER
} from '../constants';
import { useAutoHideDurationLabels } from './useAutoHideDurationLabels';

// Hook for managing map layer visibility controls
export const useLayerControl = (map, mapReady, currentMode) => {
  // Layer visibility state
  const [layerVisibility, setLayerVisibility] = useState({
    autoPath: true,
    customPath: true,
    locationPoints: true,
    durationLabels: true,
    connections: true
  });

  // Auto-hide state for duration labels
  const [autoHideInfo, setAutoHideInfo] = useState({
    isAutoHidden: false,
    shouldAutoHide: false,
    currentZoom: 12
  });

  // Handle auto-visibility changes
  const handleAutoVisibilityChange = useCallback((info) => {
    setAutoHideInfo({
      isAutoHidden: info.isAutoHidden,
      shouldAutoHide: info.shouldAutoHide,
      currentZoom: info.zoom
    });
  }, []);

  // Handle auto-enable for duration labels
  const handleDurationLabelsAutoEnable = useCallback((shouldEnable) => {
    if (shouldEnable) {
      setLayerVisibility(prev => ({
        ...prev,
        durationLabels: true
      }));
    }
  }, []);

  // Set up auto-hide for duration labels
  const { 
    isAutoHidden, 
    cityZoomThreshold, 
    shouldAutoHide,
    forceUpdateVisibility 
  } = useAutoHideDurationLabels(
    map, 
    mapReady, 
    layerVisibility.durationLabels,
    handleAutoVisibilityChange,
    handleDurationLabelsAutoEnable
  );

  // Check if layer is disabled for current mode
  const isLayerDisabledForMode = useCallback((layerKey) => {
    switch (currentMode) {
      case 'Setup Path Points':
        // Setup mode: only location points and connections
        return layerKey !== 'locationPoints' && layerKey !== 'connections';
      
      case 'Custom Path':
        // Custom mode: no auto path
        return layerKey === 'autoPath';
      
      case 'Auto Find Path':
        // Auto mode: no custom path
        return layerKey === 'customPath';
      
      case 'Connection Mode':
        // Connection mode: all layers available
        return false;
      
      case 'Comparison Mode':
        // Comparison mode: all layers available
        return false;
      
      default:
        return false;
    }
  }, [currentMode]);

  // Set visibility for individual layers
  const setLayerVisibilityInternal = useCallback((layerKey, visible) => {
    if (!map || !mapReady) {
      console.warn('Map not ready for layer control');
      return false;
    }

    try {
      switch (layerKey) {
        case 'autoPath':
          if (map.getLayer(AUTO_PATH_LAYER)) {
            map.setLayoutProperty(AUTO_PATH_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          break;


        case 'customPath':
          // Handle custom path layer
          if (map.getLayer(CUSTOM_PATH_LAYER)) {
            map.setLayoutProperty(CUSTOM_PATH_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          break;

        case 'locationPoints':
          // Control both point layers together
          if (map.getLayer(POINTS_CIRCLE_LAYER)) {
            map.setLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          if (map.getLayer(POINTS_SYMBOL_LAYER)) {
            map.setLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          break;

        case 'durationLabels':
          // Special handling for duration labels
          if (map.getLayer(DURATION_LABELS_LAYER)) {
            const currentZoom = map.getZoom();
            const shouldAutoHide = currentZoom <= 8;
            
            if (shouldAutoHide) {
              // Force hide at city level
              map.setLayoutProperty(DURATION_LABELS_LAYER, 'visibility', 'none');
              return false;
            } else {
              // Allow user control at non-city level
              map.setLayoutProperty(DURATION_LABELS_LAYER, 'visibility', visible ? 'visible' : 'none');
              // Re-evaluate auto-hide logic
              setTimeout(() => {
                if (forceUpdateVisibility) {
                  forceUpdateVisibility();
                }
              }, 10);
            }
          }
          break;

        case 'connections':
          // Handle connection lines
          if (map.getLayer(CONNECTIONS_LAYER)) {
            map.setLayoutProperty(CONNECTIONS_LAYER, 'visibility', visible ? 'visible' : 'none');
          }
          break;

        default:
          console.warn(`Unknown layer: ${layerKey}`);
          return false;
      }

      return true;
    } catch (error) {
      console.error(`Error setting ${layerKey} visibility:`, error);
      return false;
    }
  }, [map, mapReady]);

  // Toggle layer on/off
  const toggleLayer = useCallback((layerKey) => {
    // Check if layer is disabled
    if (isLayerDisabledForMode(layerKey)) {
      return false;
    }

    const newVisibility = !layerVisibility[layerKey];
    const success = setLayerVisibilityInternal(layerKey, newVisibility);
    
    if (success) {
      setLayerVisibility(prev => ({
        ...prev,
        [layerKey]: newVisibility
      }));
    }
    
    return success;
  }, [layerVisibility, setLayerVisibilityInternal, isLayerDisabledForMode]);

  // Show all available layers
  const showAllLayers = useCallback(() => {
    const updates = {};
    let hasChanges = false;
    
    Object.keys(layerVisibility).forEach(key => {
      if (!layerVisibility[key] && !isLayerDisabledForMode(key)) {
        const success = setLayerVisibilityInternal(key, true);
        if (success) {
          updates[key] = true;
          hasChanges = true;
        }
      }
    });
    
    if (hasChanges) {
      setLayerVisibility(prev => ({ ...prev, ...updates }));
    }
  }, [layerVisibility, setLayerVisibilityInternal, isLayerDisabledForMode]);

  // Hide all layers except location points
  const hideAllLayers = useCallback(() => {
    const updates = {};
    let hasChanges = false;
    
    Object.keys(layerVisibility).forEach(key => {
      // Keep location points visible
      if (key !== 'locationPoints' && layerVisibility[key] && !isLayerDisabledForMode(key)) {
        const success = setLayerVisibilityInternal(key, false);
        if (success) {
          updates[key] = false;
          hasChanges = true;
        }
      }
    });
    
    if (hasChanges) {
      setLayerVisibility(prev => ({ ...prev, ...updates }));
    }
  }, [layerVisibility, setLayerVisibilityInternal, isLayerDisabledForMode]);

  // Sync layer state with map
  const syncLayerVisibility = useCallback(() => {
    if (!map || !mapReady) return;

    try {
      const currentState = {};

      // Check auto path layer
      const autoPathLayer = map.getLayer(AUTO_PATH_LAYER);
      if (autoPathLayer) {
        currentState.autoPath = map.getLayoutProperty(AUTO_PATH_LAYER, 'visibility') !== 'none';
      }

      // Check custom path layer
      const customPathLayer = map.getLayer(CUSTOM_PATH_LAYER);
      if (customPathLayer) {
        currentState.customPath = map.getLayoutProperty(CUSTOM_PATH_LAYER, 'visibility') !== 'none';
      }


      // Check location points
      const circleLayer = map.getLayer(POINTS_CIRCLE_LAYER);
      const symbolLayer = map.getLayer(POINTS_SYMBOL_LAYER);
      if (circleLayer && symbolLayer) {
        const circleVisible = map.getLayoutProperty(POINTS_CIRCLE_LAYER, 'visibility') !== 'none';
        const symbolVisible = map.getLayoutProperty(POINTS_SYMBOL_LAYER, 'visibility') !== 'none';
        currentState.locationPoints = circleVisible && symbolVisible;
      }

      // Check duration labels layer
      const durationLayer = map.getLayer(DURATION_LABELS_LAYER);
      if (durationLayer) {
        currentState.durationLabels = map.getLayoutProperty(DURATION_LABELS_LAYER, 'visibility') !== 'none';
      }

      // Check connections layer
      const connectionsLayer = map.getLayer(CONNECTIONS_LAYER);
      if (connectionsLayer) {
        currentState.connections = map.getLayoutProperty(CONNECTIONS_LAYER, 'visibility') !== 'none';
      }

      setLayerVisibility(prev => ({ ...prev, ...currentState }));
    } catch (error) {
      console.error('Error syncing layer visibility:', error);
    }
  }, [map, mapReady]);

  // Initialize layer visibility on mount
  useEffect(() => {
    if (mapReady) {
      syncLayerVisibility();
    }
  }, [mapReady, syncLayerVisibility]);

  return {
    layerVisibility,
    toggleLayer,
    showAllLayers,
    hideAllLayers,
    syncLayerVisibility,
    isLayerDisabledForMode,
    autoHideInfo: {
      isAutoHidden,
      shouldAutoHide: shouldAutoHide(12), // Default zoom level
      currentZoom: 12
    },
    // Additional info
    currentMode
  };
};
